import { z } from "zod";
import { type graphql } from "@/graphql";
import { type CountryCode, SUPPORTED_COUNTRIES, getCountry } from "@/lib/utils/countries";
import { isValidPhoneNumber } from "@/lib/utils/phone";
import { getRequiredFieldMessage } from "@/lib/utils/validation";
import {
  createOptionalEmailSchema,
  createOptionalIntegerSchema,
  createOptionalStringSchema,
  createRequiredStringSchema,
} from "@/lib/utils/validation/zod";

export type SupplyChainPartnerRole = ReturnType<typeof graphql.scalar<"SupplyChainsSupplyChainPartnerRoleChoices">>;

export const SUPPLY_CHAIN_PARTNER_ROLES: SupplyChainPartnerRole[] = [
  "SUPPLIER",
  "CUSTOM_FEEDER",
  "OWNER_FEEDER",
  "PACKER",
  "AUCTION",
  "OTHER",
  "TRUCKING",
] as const;

const COUNTRY_CODES = Object.keys(SUPPORTED_COUNTRIES) as [CountryCode, ...CountryCode[]];

export const contactSchema = z
  .object({
    businessName: createRequiredStringSchema("business name", 50),
    type: createRequiredStringSchema("type", 50),
    role: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.enum(SUPPLY_CHAIN_PARTNER_ROLES as [SupplyChainPartnerRole, ...SupplyChainPartnerRole[]], {
        error: getRequiredFieldMessage("role"),
      }),
    ),
    contactFirstName: createOptionalStringSchema("first name", 50),
    contactLastName: createOptionalStringSchema("last name", 50),
    contactPhone: createOptionalStringSchema("phone number", 30),
    contactEmail: createOptionalEmailSchema("contact email", 50),
    businessAccountManagerId: createOptionalStringSchema("business account manager", 50),
    addressLine1: createRequiredStringSchema("address line 1", 50),
    addressLine2: createOptionalStringSchema("address line 2", 50),
    // Postcode formats vary far too much to validate. A false rejection would
    // block the exact user international addresses exist to unblock, so the
    // only rule is that there is one — checked below, under the country's own
    // name for it.
    postCode: createOptionalStringSchema("postcode", 15),
    city: createRequiredStringSchema("city", 30),
    // Free-form so a Subdivision stored before its country was supported is
    // preserved rather than rejected. Requiredness is per-country, below.
    state: createOptionalStringSchema("state", 60).optional(),
    countryCode: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.enum(COUNTRY_CODES, { error: getRequiredFieldMessage("country") }),
    ),
    accountNumber: createOptionalStringSchema("account number", 30),
    monthlyTarget: createOptionalIntegerSchema({
      displayName: "Monthly Target",
      min: 1,
      max: 1000000,
    }),
    note: createOptionalStringSchema("note", 500).optional(),
  })
  .superRefine((values, ctx) => {
    const country = getCountry(values.countryCode);

    if (!values.postCode?.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["postCode"],
        message: getRequiredFieldMessage(country?.postCodeLabel ?? "postcode"),
      });
    }

    if (country?.subdivisionRequired && !values.state?.trim()) {
      ctx.addIssue({
        code: "custom",
        path: ["state"],
        message: getRequiredFieldMessage(country.subdivisionLabel),
      });
    }

    if (!isValidPhoneNumber({ value: values.contactPhone, defaultCountry: values.countryCode })) {
      ctx.addIssue({
        code: "custom",
        path: ["contactPhone"],
        message: "Please enter a valid phone number.",
      });
    }
  });

export type ContactFormValues = z.infer<typeof contactSchema>;

/**
 * What the form is seeded with. The country is optional here and required in
 * `ContactFormValues`: a Contact saved against a country Procure does not
 * support has no code to offer, and the schema is what insists the user picks
 * one before saving.
 */
export type ContactFormInitialValues = Omit<ContactFormValues, "countryCode"> & {
  countryCode?: ContactFormValues["countryCode"];
};

/**
 * What the form hands its parent: the validated values, plus the two things
 * resolved at save time — the phone number in E.164 and the backend's primary
 * key for the chosen country.
 */
export type ContactSubmission = Omit<ContactFormValues, "contactPhone"> & {
  contactPhone: string | null;
  countryId: string;
};

export const loqateFindAddressSchema = z.object({
  Items: z.array(
    z.object({
      Id: z.string(),
      Type: z.string(),
      Text: z.string(),
      Highlight: z.string(),
      Description: z.string(),
    }),
  ),
});

/**
 * Loqate leaves `ProvinceCode` empty wherever the country's postal standard
 * identifies its Subdivision by name — Mexico returns `Jalisco` in
 * `ProvinceName`. Requiring the code would throw the moment the lookup widens
 * past the United States.
 */
export const loqateRetrieveAddressSchema = z.object({
  Items: z.array(
    z.object({
      Line1: z.string(),
      Line2: z.string().optional(),
      City: z.string(),
      PostalCode: z.string(),
      ProvinceCode: z.string().optional(),
      ProvinceName: z.string().optional(),
      Province: z.string().optional(),
    }),
  ),
});

/**
 * Loqate reports its own errors as HTTP 200 with a single item carrying an
 * `Error` code. `ky` does not throw on a 200, so the response would otherwise
 * fall through to the Zod parse and surface as "invalid data".
 */
export const loqateErrorResponseSchema = z.object({
  Items: z
    .array(
      z.object({
        Error: z.string(),
        Description: z.string(),
        Cause: z.string().optional(),
        Resolution: z.string().optional(),
      }),
    )
    .nonempty(),
});

export type LoqateFindAddress = z.infer<typeof loqateFindAddressSchema>;
export type LoqateError = z.infer<typeof loqateErrorResponseSchema>["Items"][number];

/**
 * Extracts Loqate's error item from an otherwise-successful response.
 *
 * @returns The error item, or undefined if the response is a normal result
 */
export const getLoqateError = (response: unknown): LoqateError | undefined =>
  loqateErrorResponseSchema.safeParse(response).data?.Items[0];
